/**
 * Microsoft (Entra ID) → Supabase token exchange.
 *
 * Supabase Auth can verify Azure AD ID tokens directly via
 * supabase.auth.signInWithIdToken({ provider: 'azure' }), which gives
 * Microsoft-SSO users a real Supabase session (JWT, `authenticated` role)
 * instead of running every query on the anon key.
 *
 * The catch is the nonce. gotrue's rule (token_oidc.go) is:
 *
 *   - the nonce param and the token's `nonce` claim must BOTH be present or
 *     BOTH be absent — mismatched presence is an instant 400; and
 *   - when both are present, sha256_hex(param) must equal the claim.
 *
 * MSAL puts a nonce in every authorize request, so a login WE initiate uses a
 * nonce pair we generate:
 *
 *   raw nonce    → localStorage (must survive the full-page redirect),
 *                  later passed to signInWithIdToken
 *   sha256(raw)  → MSAL request `nonce`, ends up in the ID token claim
 *
 * A token minted some other way — an MSAL refresh-token redemption, or a login
 * predating this exchange — carries either no nonce claim or one whose raw
 * value we can't know. We read the claim off the token and answer each case on
 * its own terms rather than blindly attaching whatever is in storage.
 *
 * The raw nonce is deliberately NOT consumed on success: it stays valid for as
 * long as the token it was minted with, and MSAL hands that same cached token
 * back on every later acquireTokenSilent. Clearing it stranded returning users
 * — once their Supabase session lapsed, the bridge could never re-exchange the
 * cached token and the app sat on "Still verifying access…" forever. The next
 * login overwrites it.
 */

import { supabase } from '@/lib/supabase';
import { isUserBannedError } from '@/lib/authErrors';

const RAW_NONCE_STORAGE_KEY = 'flow_ms_login_nonce';

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return bytesToHex(new Uint8Array(digest));
}

/**
 * Generate a nonce pair for a Microsoft login. Stores the raw nonce and
 * returns the hashed nonce to pass as the MSAL request's `nonce`.
 */
export async function createLoginNonce(): Promise<string> {
  const raw = bytesToHex(crypto.getRandomValues(new Uint8Array(32)));
  localStorage.setItem(RAW_NONCE_STORAGE_KEY, raw);
  return sha256Hex(raw);
}

/**
 * Read the `nonce` claim from an ID token without verifying it. The signature
 * is Supabase's job; all we need locally is whether to send a nonce param at
 * all. Anything unparseable reads as "no claim", which is a state gotrue
 * answers definitively rather than a crash inside the bridge effect.
 */
function readNonceClaim(idToken: string): string | null {
  try {
    const payload = idToken.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    const json = new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
    const nonce = (JSON.parse(json) as { nonce?: unknown }).nonce;
    return typeof nonce === 'string' && nonce !== '' ? nonce : null;
  } catch {
    return null;
  }
}

/**
 * Why an exchange couldn't produce a Supabase session.
 *
 *   missing-nonce  → the token needs a nonce param and we have none stored;
 *                    only a fresh authorize round-trip can fix it
 *   nonce-mismatch → the stored nonce belongs to a different login; likewise
 *   signup-disabled→ gotrue refused to CREATE the account. The identity is
 *                    fine; the account was never provisioned. No amount of
 *                    retrying helps — an admin has to add them.
 *   user-banned    → gotrue refused an account that EXISTS: a deactivated
 *                    person's login is banned (migration 274). The identity is
 *                    fine; the access was removed on purpose. Retrying cannot
 *                    help either — an admin has to restore it.
 *   exchange-failed→ anything else (transport, gotrue outage, bad config)
 */
export type ExchangeFailureReason =
  | 'missing-nonce'
  | 'nonce-mismatch'
  | 'signup-disabled'
  | 'user-banned'
  | 'exchange-failed';

export type ExchangeResult =
  | { ok: true }
  | { ok: false; reason: ExchangeFailureReason; error?: string };

/** gotrue answers an unprovisioned identity with a 422 signup_disabled. */
function isSignupDisabled(error: { message?: string; code?: string; status?: number }): boolean {
  return (
    error.code === 'signup_disabled' ||
    /signups?\s+not\s+allowed/i.test(error.message ?? '')
  );
}

/**
 * Exchange a Microsoft ID token for a Supabase session.
 *
 * authStore's onAuthStateChange picks up the resulting session, so callers
 * only need to handle failure — and the reason matters: a 'signup-disabled'
 * account is a provisioning gap to report, while a missing nonce just needs
 * another trip through Entra.
 */
export async function exchangeMicrosoftIdToken(idToken: string): Promise<ExchangeResult> {
  const tokenNonce = readNonceClaim(idToken);
  let nonce: string | undefined;

  if (tokenNonce) {
    const rawNonce = localStorage.getItem(RAW_NONCE_STORAGE_KEY);
    if (!rawNonce) {
      return { ok: false, reason: 'missing-nonce' };
    }
    // Check the pairing here rather than spending a round-trip to be told
    // "Nonces mismatch" — and so the caller can tell the two apart.
    if ((await sha256Hex(rawNonce)) !== tokenNonce) {
      return { ok: false, reason: 'nonce-mismatch' };
    }
    nonce = rawNonce;
  }

  const { error } = await supabase.auth.signInWithIdToken({
    provider: 'azure',
    token: idToken,
    // Omitted entirely when the token has no claim — gotrue rejects a param
    // paired with a claimless token.
    ...(nonce ? { nonce } : {}),
  });

  if (error) {
    const reason: ExchangeFailureReason = isSignupDisabled(error)
      ? 'signup-disabled'
      : isUserBannedError(error)
        ? 'user-banned'
        : 'exchange-failed';
    console.error(`[microsoftTokenExchange] signInWithIdToken failed (${reason}):`, error.message);
    return { ok: false, reason, error: error.message };
  }

  return { ok: true };
}

/** True when a Microsoft-authenticated user has no Supabase session yet. */
export async function hasSupabaseSession(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return !!data.session;
}
